// LA DIVERSITÉ DU CHOIX DES VISUELS (15/10/2026).
//
// Mesuré sur 14 jours (apparitions plafonnées à 10 min — des consoles
// restées ouvertes en pause gonflaient certaines à 16 h) : les dix visuels
// les plus vus faisaient 20 % du temps ; la simulation de l'ancien système
// sur les 7 758 morceaux réels de 30 jours en donne 34 %, le nouveau 13 %. Trois causes, une
// réponse chacune, toutes ici pour être testées hors navigateur
// (tools/simuler-diversite.mjs) :
//
// 1. L'ÉCHELLE. Les morceaux reçoivent presque toujours 0,5, 0,6, 0,7 ou 0,8 ;
//    le catalogue s'entasse entre 0,7 et 0,95. Les quinze « plus proches
//    au-dessus » d'un indice tenaient dans 0,05 de dansabilité. Un étalement
//    du catalogue à rang égal a été ESSAYÉ et écarté (simulation du
//    15/10/2026) : il n'ajoutait qu'un point de diversité et décalait les
//    visuels de +0,11 au-dessus des morceaux. La bande ci-dessous suffit.
// 2. LE VIVIER FIGÉ. Même indice, mêmes quinze : quatre indices courants,
//    quatre viviers. → vivierBande : tous les visuels d'une BANDE autour de
//    l'indice (plus large vers le haut), tirés au sort avec un poids qui
//    favorise les plus proches, la graine du morceau rendant le tirage stable
//    pendant un titre et différent d'un titre à l'autre. Deux places sur
//    quinze sont tirées dans une bande plus large : l'exploration.
// 3. LE MÊME GAGNANT DANS LE VIVIER. → fraicheur : chaque écran se souvient de
//    ce qu'il a montré (demi-vie de 90 minutes) ; plus un visuel a été vu,
//    moins il pèse. Rien n'est banni, tout tourne.
//
// Ce qui ne change pas : la porte d'éligibilité, les exclusions de mode (pas
// d'intense sur un pont), la règle « jamais deux fois le même dans un titre ».

export const BANDE_BAS = 0.12;      // sous l'indice
export const BANDE_HAUT = 0.20;     // au-dessus : un morceau qui danse peut monter
export const SIGMA_BAS = 0.07;
export const SIGMA_HAUT = 0.10;
export const EXPLORATION = 2;       // places du vivier tirées dans la bande large
export const BANDE_LARGE = 0.25;
export const DEMI_VIE_MS = 90 * 60 * 1000;
export const POIDS_EXPOSITION = 0.6;

// --- un hasard reproductible -------------------------------------------------
function hacher(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
function aleatoire(graine) {
  let a = hacher(String(graine)) || 1;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Tirage pondéré SANS remise (méthode d'Efraimidis-Spirakis : clé u^(1/w)).
function tirer(liste, poids, n, rnd) {
  return liste.map((v, i) => ({ v, k: poids[i] > 0 ? Math.pow(rnd(), 1 / poids[i]) : -1 }))
    .filter((x) => x.k >= 0).sort((a, b) => b.k - a.k).slice(0, n).map((x) => x.v);
}

// --- 2. le vivier en bande ---------------------------------------------------
// index : la dansabilité du morceau (0..1) ; size : la taille du vivier ;
// seed : la graine du morceau (stable pendant une écoute). Les visuels sans
// dansabilité ne sont jamais tirés (ils ne se placent pas sur l'axe).
export function vivierBande(library, index, size, seed) {
  const lib = library || [];
  if (index == null) return lib.slice();
  if (lib.length <= size) return lib.slice();
  const places = lib.filter((v) => v && v.danceability != null);
  const d = (v) => v.danceability - index;
  const poids = (v) => {
    const x = d(v);
    const s = x >= 0 ? SIGMA_HAUT : SIGMA_BAS;
    return Math.exp(-(x * x) / (2 * s * s));
  };
  const rnd = aleatoire('vivier|' + seed);
  // LES RÉSERVÉS D'ABORD : un visuel qui ne passe la porte d'éligibilité que
  // sur un déclenchement (countdown, requires.dropTrigger) n'est dans la
  // bibliothèque reçue QUE quand l'orchestrateur le lance lui-même — il doit
  // alors être au vivier, pas laissé au hasard du tirage.
  const reserves = places.filter((v) => v.requires && v.requires.dropTrigger).slice(0, size);
  const resIds = new Set(reserves.map((v) => v.id));
  const libres = places.filter((v) => !resIds.has(v.id));
  const bande = libres.filter((v) => d(v) >= -BANDE_BAS && d(v) <= BANDE_HAUT);
  const nExplo = Math.min(EXPLORATION, Math.max(0, size - reserves.length - 1));
  let vivier = reserves.concat(tirer(bande, bande.map(poids), size - reserves.length - nExplo, rnd));
  // L'exploration : hors de ce qui est déjà pris, dans la bande large, au
  // poids uniforme — c'est elle qui fait voir ce que la note seule n'aurait
  // jamais retenu.
  const pris = new Set(vivier.map((v) => v.id));
  // Vers le HAUT surtout : l'orchestrateur ne redescend pas sur un morceau
  // qui danse (voir « VERS LE HAUT, JAMAIS VERS LE BAS », orchestrator.js).
  const large = libres.filter((v) => !pris.has(v.id) && d(v) >= -BANDE_BAS / 2 && d(v) <= BANDE_LARGE);
  vivier = vivier.concat(tirer(large, large.map(() => 1), nExplo, rnd));
  // Une bande trop maigre (haut ou bas de l'échelle) : on complète par les
  // plus proches, comme avant. Un vivier tiède vaut mieux qu'un vivier vide.
  if (vivier.length < size) {
    const deja = new Set(vivier.map((v) => v.id));
    const reste = places.filter((v) => !deja.has(v.id)).sort((a, b) => Math.abs(d(a)) - Math.abs(d(b)));
    vivier = vivier.concat(reste.slice(0, size - vivier.length));
  }
  // Rendu dans l'ordre de dansabilité RÉELLE, décroissante : la console
  // l'affiche ainsi, et l'ordre ne doit rien au tirage.
  return vivier.sort((a, b) => b.danceability - a.danceability || (a.id < b.id ? -1 : 1));
}

// --- 3. la fraîcheur -----------------------------------------------------------
// L'exposition d'un visuel : un compteur qui décroît (demi-vie DEMI_VIE_MS) et
// prend +1 chaque fois qu'il passe à l'écran. Gardée par écran
// (localStorage) : la télé du salon tourne sur son propre historique.
const CLE = 'halofloor_exposition';
let EXPO = null;
let maintenant = () => Date.now();
export function reglerHorloge(f) { maintenant = f; EXPO = null; }   // tests et simulation
function stockage() { try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch (e) { return null; } }
function charger() {
  if (EXPO) return EXPO;
  EXPO = {};
  const s = stockage();
  if (s) { try { EXPO = JSON.parse(s.getItem(CLE) || '{}') || {}; } catch (e) { EXPO = {}; } }
  return EXPO;
}
function valeur(e, t) { return e ? e.v * Math.pow(0.5, (t - e.t) / DEMI_VIE_MS) : 0; }
export function exposition(id) { return valeur(charger()[id], maintenant()); }
export function noterExposition(id) {
  if (!id) return;
  const ex = charger(), t = maintenant();
  ex[id] = { v: valeur(ex[id], t) + 1, t };
  // Élagage : ce qui est retombé sous 0,05 n'a plus d'effet.
  for (const k of Object.keys(ex)) if (valeur(ex[k], t) < 0.05) delete ex[k];
  const s = stockage();
  if (s) { try { s.setItem(CLE, JSON.stringify(ex)); } catch (e) { /* plein ou refusé : en mémoire */ } }
}
export function oublierExpositions() { EXPO = {}; const s = stockage(); if (s) { try { s.removeItem(CLE); } catch (e) { /* rien */ } } }
// Le facteur appliqué au score : 1 pour un visuel jamais vu récemment,
// 1/(1 + 0,6·exposition) ensuite — vu trois fois dans l'heure, il pèse 0,36.
export function fraicheur(id) { return 1 / (1 + POIDS_EXPOSITION * exposition(id)); }

// --- dans le vivier : la fenêtre autour de la cible d'intensité -------------
// bandeIntensite (orchestrator.js) prenait LE plus proche de la cible, à
// 0,005 près : à intensité égale, le même visuel à chaque fois. Les candidats
// à FENETRE_INTENSITE près de la cible restent en lice, et c'est la fraîcheur
// (dans le score) qui les départage.
// En FRACTION DE LA PLAGE du vivier (pas en dansabilité absolue) : un vivier
// plus étalé ne doit pas laisser une vraie baisse d'intensité retomber sur un
// visuel aussi fort (tools/test-intensite.mjs, « une vraie baisse redescend »).
export const FENETRE_INTENSITE = 0.10;
// NULLE AUX EXTRÊMES : le drop principal (intensité 1) garde LE visuel le plus
// haut du vivier, l'ouverture (0) le plus calme — c'est voulu
// (tools/test-intensite.mjs). La latitude est entière à mi-intensité, là où
// se jouent la plupart des changements.
export function fenetreIntensite(intensite, plage) {
  const i = Math.max(0, Math.min(1, Number(intensite) || 0));
  const p = plage != null && isFinite(plage) ? Math.max(0, plage) : 0.15;
  return Math.max(0.005, FENETRE_INTENSITE * p * 4 * i * (1 - i));
}
